import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tip } from '@/components/ui/tip';
import { ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import ProviderCombobox from '@/components/ProviderCombobox';
import { prettyCategory } from '@/utils/labels';
import { getImageUrl } from '@/utils/imagePaths';
import { ArrowLeftIcon, PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/contexts/AuthContext';
import VehicleFitmentInput from '@/components/VehicleFitmentInput';
import { DatePicker } from '@/components/ui/date-picker';
import { hoyLocal } from '@/utils/fechaLocal';

type Product = {
  id: string;
  name: string;
  description?: string;
  brand?: string;
  model?: string;
  tireMeasure?: string;
  loadIndex?: string;
  speedIndex?: string;
  vehicleFitment?: any;
  category: string;
  sku?: string;
  costPrice?: number;
  listPrice?: number;
  lastCost?: number;
  mainSupplierName?: string;
  supplierSku?: string;
  salePrice: number;
  sellingPrice?: number;
  stock?: number;
  currentStock?: number;
  minStock?: number;
  minimumStock?: number;
  unit?: string;
  location?: string;
  isActive?: boolean;
  isService?: boolean;
  imageUrl?: string | null;
  datasheetUrl?: string | null;
  tags?: string[];
  recommendedApps?: string[];
  specsUpdatedAt?: string | null;
  requiredRole?: string;
  requiredSkills?: string[];
  requiredStaff?: number;
  status?: 'active' | 'paused' | 'discontinued';
  variantGroupId?: string;
  variantLabel?: string;
  variantAttributes?: Record<string, string>;
  isBundle?: boolean;
  bundleItems?: Array<{ productId: string; name: string; qty: number; unitPrice?: number }>;
  bundlePriceMode?: 'fixed' | 'sum';
  // Campos especificos por categoria
  viscosity?: string;
  oilBaseType?: string;
  capacity?: string;
  apiSpec?: string;
  lubricantType?: string;
  partNumber?: string;
  estimatedDuration?: number;
  workshopStationKey?: string;
  markupPercent?: number; // Solo UI - no se persiste; se calcula desde cost+sale
};

const emptyProduct: Product = {
  id: '',
  name: '',
  description: '',
  brand: '',
  model: '',
  tireMeasure: '',
  loadIndex: '',
  speedIndex: '',
  vehicleFitment: '',
  category: '',
  sku: '',
  costPrice: undefined,
  listPrice: undefined,
  lastCost: undefined,
  mainSupplierName: '',
  supplierSku: '',
  salePrice: 0,
  stock: 0,
  minStock: 0,
  unit: '',
  location: '',
  isActive: true,
  isService: false,
  imageUrl: '',
  datasheetUrl: '',
  tags: [] as string[],
  recommendedApps: [] as string[],
  requiredRole: '',
  requiredSkills: [],
  requiredStaff: 1,
  status: 'active',
  variantGroupId: '',
  variantLabel: '',
  variantAttributes: {},
  isBundle: false,
  bundleItems: [],
  bundlePriceMode: 'fixed',
  viscosity: '',
  oilBaseType: '',
  capacity: '',
  apiSpec: '',
  lubricantType: '',
  partNumber: '',
  estimatedDuration: undefined,
  workshopStationKey: 'taller'
};

const ROLE_OPTIONS = [
  { value: 'mecanico', label: 'Mecánico' },
  { value: 'operario', label: 'Operario' },
  { value: 'recepcionista', label: 'Recepcionista' },
  { value: 'cajero', label: 'Cajero' },
  { value: 'admin', label: 'Admin' }
];

const SKILL_OPTIONS = [
  { value: 'mecanico', label: 'Mecánica general' },
  { value: 'gomeria', label: 'Gomería' },
  { value: 'taller', label: 'Taller' },
  { value: 'lavadero', label: 'Lavadero' },
  { value: 'lubricentro', label: 'Lubricentro' },
  { value: 'diagnostico', label: 'Diagnóstico' },
  { value: 'electricidad', label: 'Electricidad' },
  { value: 'alineacion', label: 'Alineación' },
  { value: 'balanceo', label: 'Balanceo' }
];

const normalizeCategoryInput = (value: string) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s_-]/g, '')
    .trim()
    .replace(/\s+/g, '_');

const formatCategoryLabel = (value: string) => {
  const key = normalizeCategoryInput(value);
  const labels: Record<string, string> = {
    servicio: 'Servicio',
    neumaticos: 'Neumaticos',
    aceites: 'Aceites',
    filtros: 'Filtros',
    frenos: 'Frenos',
    motor: 'Motor',
    transmision: 'Transmision',
    electrico: 'Electrico',
    carroceria: 'Carroceria',
    herramientas: 'Herramientas',
    consumibles: 'Consumibles',
    otros: 'Otros'
  };
  return labels[key] || value;
};

const ProductFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const returnToParam = searchParams.get('returnTo');
  const prefillNameParam = (searchParams.get('name') || '').trim();
  const backTarget = returnToParam && returnToParam.startsWith('/') ? returnToParam : '/products';
  const productFromState = (location.state as { product?: Product } | undefined)?.product;
  const isEdit = Boolean(id);
  const { hasRole } = useAuth();
  const showFinancials = hasRole(['admin']);

  const [form, setForm] = useState<Product>(emptyProduct);
  const [categories, setCategories] = useState<string[]>([]);
  const [units, setUnits] = useState<string[]>([]);
  const [locations, setLocations] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(Boolean(isEdit && !productFromState));
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [costHistory, setCostHistory] = useState<Array<{
    id: string;
    costPrice: number;
    previousCostPrice?: number | null;
    providerName?: string | null;
    createdAt: string;
  }>>([]);
  const [supplierPrices, setSupplierPrices] = useState<Array<{
    id: string;
    providerName: string;
    costPrice: number;
    isFavorite?: number;
    updatedAt?: string;
  }>>([]);
  const [providers, setProviders] = useState<Array<{ id: string; name: string }>>([]);
  useEffect(() => {
    axios.get('/api/providers', { params: { page: 1, limit: 300, isActive: true } })
      .then(r => setProviders(r.data?.providers || []))
      .catch(() => {});
  }, []);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [bundleCandidateId, setBundleCandidateId] = useState('');
  const [bundleCandidateQty, setBundleCandidateQty] = useState('1');
  const [bundleCandidateText, setBundleCandidateText] = useState('');
  const [skillOptions, setSkillOptions] = useState<{ value: string; label: string; isActive?: boolean }[]>(SKILL_OPTIONS);
  const [skillPickerOpen, setSkillPickerOpen] = useState(false);
  const [skillQuery, setSkillQuery] = useState('');
  const [stationOptions, setStationOptions] = useState<{ type: string; name: string }[]>([
    { type: 'taller', name: 'Taller' },
    { type: 'gomeria', name: 'Gomeria' },
    { type: 'lubricentro', name: 'Lubricentro' },
    { type: 'lavadero', name: 'Lavadero' }
  ]);

  useEffect(() => {
    let cancelled = false;
    axios.get('/api/wes/stations')
      .then((res) => {
        const list = (res.data?.stations || []).map((s: any) => ({ type: s.type, name: s.name }));
        if (!cancelled && list.length > 0) setStationOptions(list);
      })
      .catch(() => { /* fallback a las 4 por defecto */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    axios.get('/api/skills/catalog')
      .then((res) => {
        const list = (res.data?.skills || []).map((s: any) => ({ value: s.value, label: s.label, isActive: s.isActive !== false }));
        if (!cancelled && list.length > 0) setSkillOptions(list);
      })
      .catch(() => { /* fallback a SKILL_OPTIONS */ });
    return () => { cancelled = true; };
  }, []);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const normalizedCat = String(form.category || '').toLowerCase();
  const isTireCategory = normalizedCat === 'neumaticos' || normalizedCat === 'neumático' || normalizedCat === 'cubiertas';
  const isOilCategory = normalizedCat === 'aceites' || normalizedCat === 'aceite';
  // 'lubricantes' se unificó con 'aceites'; los fluidos que quedan fuera de esa
  // categoría (líquido de freno, refrigerante) igual llevan tipo y capacidad
  const isLubricantCategory = normalizedCat === 'frenos' || normalizedCat === 'consumibles'
    || normalizedCat === 'lubricantes' || normalizedCat === 'liquidos' || normalizedCat === 'grasas';
  const isPartCategory = normalizedCat === 'repuestos' || normalizedCat === 'repuesto';
  const isServiceType = form.category === 'servicio' || form.isService;
  const toMillis = (value: string | undefined | null) => {
    const ms = value ? new Date(value).getTime() : 0;
    return Number.isFinite(ms) ? ms : 0;
  };

  const fetchCategories = useCallback(async () => {
    try {
      // Fuente única: la tabla de categorías gestionada (ya cubre todas las en uso)
      const catalogRes = await axios.get('/api/categories?type=product');
      const merged = new Set<string>();
      const catalogCategories = catalogRes.data?.categories || [];
      catalogCategories.forEach((cat: any) => {
        const normalized = normalizeCategoryInput(cat?.name || cat || '');
        if (normalized) merged.add(normalized);
      });

      merged.add('servicio');
      setCategories(Array.from(merged).sort((a, b) => a.localeCompare(b)));
    } catch (error) {
      console.error('Error fetching categories', error);
      setCategories(['servicio', 'neumaticos', 'aceites', 'filtros', 'frenos', 'otros']);
    }
  }, []);

  const fetchUnits = useCallback(async () => {
    try {
      const res = await axios.get('/api/products/units');
      setUnits(res.data?.units || []);
    } catch (error) {
      console.error('Error fetching units', error);
    }
  }, []);

  const fetchLocations = useCallback(async () => {
    try {
      const res = await axios.get('/api/settings/product-locations');
      const list = res.data?.locations || [];
      setLocations(list.map((loc: any) => loc.name).filter(Boolean));
    } catch (error) {
      console.error('Error fetching product locations', error);
      setLocations([]);
    }
  }, []);

  const fetchProduct = useCallback(async (productId: string) => {
    try {
      setLoading(true);
      const res = await axios.get(`/api/products/${productId}`);
      const product = res.data?.product || res.data;
      if (!product) {
        toast.error('Producto no encontrado');
        navigate(backTarget);
        return;
      }
      const parsedRequiredSkills = (() => {
        if (Array.isArray(product.requiredSkills)) return product.requiredSkills;
        if (typeof product.requiredSkills === 'string') {
          try {
            const parsed = JSON.parse(product.requiredSkills);
            return Array.isArray(parsed) ? parsed : [];
          } catch {
            return [];
          }
        }
        return [];
      })();
      const parsedBundleItems = (() => {
        if (Array.isArray(product.bundleItems)) return product.bundleItems;
        if (typeof product.bundleItems === 'string') {
          try {
            const parsed = JSON.parse(product.bundleItems);
            return Array.isArray(parsed) ? parsed : [];
          } catch {
            return [];
          }
        }
        return [];
      })();
      setForm({
        ...emptyProduct,
        ...product,
        category: product.category || '',
        sku: product.sku || '',
        salePrice: product.salePrice ?? product.sellingPrice ?? 0,
        stock: product.stock ?? product.currentStock ?? 0,
        minStock: product.minStock ?? product.minimumStock ?? 0,
        costPrice: product.costPrice ?? product.purchasePrice ?? undefined,
        listPrice: product.listPrice ?? undefined,
        lastCost: product.lastCost ?? product.costPrice ?? undefined,
        brand: product.brand || '',
        model: product.model || '',
        tireMeasure: product.tireMeasure || '',
        loadIndex: product.loadIndex || '',
        speedIndex: product.speedIndex || '',
        mainSupplierName: product.mainSupplierName || '',
        supplierSku: (product as any).supplierSku || '',
        vehicleFitment: product.vehicleFitment || '',
        location: product.location || '',
        isActive: product.isActive !== false,
        isService: product.category === 'servicio' || product.isService === true,
        imageUrl: product.imageUrl || '',
        datasheetUrl: (product as any).datasheetUrl || '',
        tags: Array.isArray((product as any).tags) ? (product as any).tags : [],
        recommendedApps: Array.isArray((product as any).recommendedApps) ? (product as any).recommendedApps : [],
        specsUpdatedAt: (product as any).specsUpdatedAt || null,
        requiredRole: product.requiredRole || '',
        requiredSkills: parsedRequiredSkills,
        requiredStaff: product.requiredStaff ?? 1,
        status: product.status || (product.isActive !== false ? 'active' : 'paused'),
        variantGroupId: product.variantGroupId || '',
        variantLabel: product.variantLabel || '',
        variantAttributes: product.variantAttributes || {},
        isBundle: Boolean(product.isBundle),
        bundleItems: parsedBundleItems,
        bundlePriceMode: product.bundlePriceMode || 'fixed',
        viscosity: (product as any).viscosity || '',
        oilBaseType: (product as any).oilBaseType || '',
        capacity: (product as any).capacity || '',
        apiSpec: (product as any).apiSpec || '',
        lubricantType: (product as any).lubricantType || '',
        partNumber: (product as any).partNumber || '',
        estimatedDuration: (product as any).estimatedDuration ?? undefined,
        workshopStationKey: (product as any).workshopStationKey || 'taller',
        markupPercent: (() => {
          const cost = Number(product.costPrice ?? (product as any).purchasePrice ?? 0);
          const sale = Number(product.salePrice ?? product.sellingPrice ?? 0);
          if (cost > 0 && sale > 0) return Math.round(((sale - cost) / cost) * 10000) / 100;
          return undefined;
        })() as any
      });
      setImagePreview(product.imageUrl || null);
    } catch (error) {
      console.error('Error loading product', error);
      toast.error('No se pudo cargar el producto');
      navigate(backTarget);
    } finally {
      setLoading(false);
    }
  }, [navigate, backTarget]);

  useEffect(() => {
    fetchCategories();
    fetchUnits();
    fetchLocations();
  }, [fetchCategories, fetchUnits, fetchLocations]);

  useEffect(() => {
    if (isEdit) {
      if (productFromState) {
        const parsedRequiredSkills = (() => {
          if (Array.isArray(productFromState.requiredSkills)) return productFromState.requiredSkills;
          if (typeof productFromState.requiredSkills === 'string') {
            try {
              const parsed = JSON.parse(productFromState.requiredSkills);
              return Array.isArray(parsed) ? parsed : [];
            } catch {
              return [];
            }
          }
          return [];
        })();
        const parsedBundleItems = (() => {
          if (Array.isArray(productFromState.bundleItems)) return productFromState.bundleItems;
          if (typeof (productFromState as any).bundleItems === 'string') {
            try {
              const parsed = JSON.parse((productFromState as any).bundleItems);
              return Array.isArray(parsed) ? parsed : [];
            } catch {
              return [];
            }
          }
          return [];
        })();
        setForm({
          ...emptyProduct,
          ...productFromState,
          salePrice: productFromState.salePrice ?? productFromState.sellingPrice ?? 0,
          stock: productFromState.stock ?? productFromState.currentStock ?? 0,
          minStock: productFromState.minStock ?? productFromState.minimumStock ?? 0,
          listPrice: productFromState.listPrice ?? undefined,
          brand: productFromState.brand || '',
          model: productFromState.model || '',
          tireMeasure: productFromState.tireMeasure || '',
          loadIndex: productFromState.loadIndex || '',
          speedIndex: productFromState.speedIndex || '',
          isActive: productFromState.isActive !== false,
          isService: productFromState.category === 'servicio' || productFromState.isService === true,
          lastCost: productFromState.lastCost ?? productFromState.costPrice ?? undefined,
          mainSupplierName: productFromState.mainSupplierName || '',
          supplierSku: (productFromState as any).supplierSku || '',
          vehicleFitment: productFromState.vehicleFitment || '',
          location: productFromState.location || '',
          requiredRole: productFromState.requiredRole || '',
          requiredSkills: parsedRequiredSkills,
          requiredStaff: productFromState.requiredStaff ?? 1,
          status: productFromState.status || (productFromState.isActive !== false ? 'active' : 'paused'),
          variantGroupId: productFromState.variantGroupId || '',
          variantLabel: productFromState.variantLabel || '',
          variantAttributes: productFromState.variantAttributes || {},
          isBundle: Boolean(productFromState.isBundle),
          bundleItems: parsedBundleItems,
          bundlePriceMode: productFromState.bundlePriceMode || 'fixed',
          viscosity: (productFromState as any).viscosity || '',
          oilBaseType: (productFromState as any).oilBaseType || '',
          capacity: (productFromState as any).capacity || '',
          apiSpec: (productFromState as any).apiSpec || '',
          lubricantType: (productFromState as any).lubricantType || '',
          partNumber: (productFromState as any).partNumber || '',
          estimatedDuration: (productFromState as any).estimatedDuration ?? undefined,
          workshopStationKey: (productFromState as any).workshopStationKey || 'taller',
          markupPercent: (() => {
            const cost = Number(productFromState.costPrice ?? (productFromState as any).purchasePrice ?? 0);
            const sale = Number(productFromState.salePrice ?? productFromState.sellingPrice ?? 0);
            if (cost > 0 && sale > 0) return Math.round(((sale - cost) / cost) * 10000) / 100;
            return undefined;
          })() as any
        });
        setImagePreview(productFromState.imageUrl || null);
        setLoading(false);
      } else if (id) {
        fetchProduct(id);
      }
    } else {
      setForm({
        ...emptyProduct,
        name: prefillNameParam || ''
      });
      setImagePreview(null);
      setLoading(false);
    }
  }, [isEdit, id, productFromState, fetchProduct, prefillNameParam]);

  const fetchCostHistory = useCallback(async (productId: string) => {
    try {
      const [historyRes, supplierRes] = await Promise.all([
        axios.get(`/api/products/${productId}/cost-history?limit=20`),
        axios.get(`/api/products/${productId}/supplier-prices`)
      ]);
      setCostHistory(historyRes.data?.history || []);
      setSupplierPrices(supplierRes.data?.supplierPrices || []);
    } catch {
      setCostHistory([]);
      setSupplierPrices([]);
    }
  }, []);

  const fetchProductsForBundle = useCallback(async () => {
    try {
      const res = await axios.get('/api/products?page=1&limit=1000');
      const list: Product[] = res.data?.products || [];
      setAllProducts(list.filter((item) => item.id !== id));
    } catch {
      setAllProducts([]);
    }
  }, [id]);

  useEffect(() => {
    fetchProductsForBundle();
  }, [fetchProductsForBundle]);

  useEffect(() => {
    if (isEdit && id) {
      fetchCostHistory(id);
    } else {
      setCostHistory([]);
      setSupplierPrices([]);
    }
  }, [isEdit, id, fetchCostHistory]);

  const inferHistoryProvider = useCallback((entry: { providerName?: string | null; costPrice: number }) => {
    return (
      String(entry.providerName || '').trim() ||
      supplierPrices.find((row) => Number(row.costPrice || 0) === Number(entry.costPrice || 0))?.providerName ||
      ''
    );
  }, [supplierPrices]);

  const vigenteHistoryId = useMemo(() => {
    const currentProvider = String(form.mainSupplierName || '').trim().toLowerCase();
    const currentCost = Number(form.costPrice || 0);
    const candidates = costHistory
      .map((entry) => ({
        entry,
        provider: String(inferHistoryProvider(entry) || '').trim().toLowerCase()
      }))
      .filter((item) => Number(item.entry.costPrice || 0) === currentCost && item.provider === currentProvider)
      .sort((a, b) => toMillis(b.entry.createdAt) - toMillis(a.entry.createdAt));
    return candidates[0]?.entry.id || null;
  }, [costHistory, form.costPrice, form.mainSupplierName, inferHistoryProvider]);

  const handleChange = (field: keyof Product, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const categoryOptions = useMemo(() => {
    const unique = Array.from(new Set((categories || []).map((cat) => normalizeCategoryInput(cat)).filter(Boolean)));
    if (isServiceType) {
      return unique.includes('servicio') ? ['servicio'] : [];
    }
    return unique.filter((cat) => cat !== 'servicio');
  }, [categories, isServiceType]);

  useEffect(() => {
    if (isServiceType) {
      if (form.category !== 'servicio') {
        setForm((prev) => ({ ...prev, category: 'servicio', isService: true }));
      }
      return;
    }
    if (form.category === 'servicio') {
      const fallbackCategory = categoryOptions[0] || 'neumaticos';
      setForm((prev) => ({ ...prev, category: fallbackCategory, isService: false }));
    }
  }, [isServiceType, form.category, categoryOptions]);

  const toggleRequiredSkill = (skill: string) => {
    setForm((prev) => {
      const current = Array.isArray(prev.requiredSkills) ? prev.requiredSkills : [];
      if (current.includes(skill)) {
        return { ...prev, requiredSkills: current.filter((s) => s !== skill) };
      }
      return { ...prev, requiredSkills: [...current, skill] };
    });
  };

  // Agregar idempotente (para el combobox: disparar dos veces no duplica)
  const addRequiredSkill = (skill: string) => {
    setForm((prev) => {
      const current = Array.isArray(prev.requiredSkills) ? prev.requiredSkills : [];
      if (current.includes(skill)) return prev;
      return { ...prev, requiredSkills: [...current, skill] };
    });
    setSkillPickerOpen(false);
    setSkillQuery('');
  };

  const addBundleItem = () => {
    const selected = allProducts.find((item) => item.id === bundleCandidateId);
    if (!selected) return;
    const qty = Math.max(1, Number(bundleCandidateQty || 1));
    setForm((prev) => {
      const current = Array.isArray(prev.bundleItems) ? prev.bundleItems : [];
      const idx = current.findIndex((it) => it.productId === selected.id);
      if (idx >= 0) {
        const next = [...current];
        next[idx] = { ...next[idx], qty: Number(next[idx].qty || 0) + qty };
        return { ...prev, bundleItems: next };
      }
      return {
        ...prev,
        bundleItems: [
          ...current,
          {
            productId: selected.id,
            name: selected.name,
            qty,
            unitPrice: Number(selected.salePrice || 0)
          }
        ]
      };
    });
    setBundleCandidateId('');
    setBundleCandidateQty('1');
  };

  const removeBundleItem = (productId: string) => {
    setForm((prev) => ({
      ...prev,
      bundleItems: (prev.bundleItems || []).filter((item) => item.productId !== productId)
    }));
  };

  // Inclusión escrita a mano (no hace falta que exista como servicio del
  // catálogo): solo describe qué lleva el combo
  const addBundleTexto = () => {
    const label = bundleCandidateText.trim();
    if (!label) return;
    setForm((prev) => {
      const current = Array.isArray(prev.bundleItems) ? prev.bundleItems : [];
      if (current.some((it) => String(it.name).toLowerCase() === label.toLowerCase())) return prev;
      return {
        ...prev,
        bundleItems: [...current, { productId: `texto:${label.toLowerCase()}`, name: label, qty: 1 }]
      };
    });
    setBundleCandidateText('');
  };

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten imágenes');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no puede superar 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', file);

      // Usar directamente el endpoint de insumos (ya probado y estable) para evitar el timeout en productos
      const res = await axios.post('/api/supplies/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 20000
      });
      const uploadedUrl = res.data?.imageUrl;

      if (uploadedUrl) {
        setImagePreview(uploadedUrl);
        handleChange('imageUrl', uploadedUrl);
        // Guardar de inmediato la URL si estamos editando para no depender del submit
        if (isEdit && id) {
          try {
            await axios.put(`/api/products/${id}`, { imageUrl: uploadedUrl });
          } catch (saveErr: any) {
            console.error('Error guardando imageUrl inmediato:', saveErr);
            toast.error(saveErr?.response?.data?.error || 'No se pudo guardar la imagen en el producto');
          }
        }
        toast.success('Imagen subida correctamente');
      } else {
        toast.error('No se pudo obtener la URL de la imagen');
      }
    } catch (error: any) {
      console.error('Error uploading image:', error);
      toast.error(error?.response?.data?.error || 'Error al subir la imagen');
    } finally {
      setUploadingImage(false);
    }
  };


  // Ficha técnica (PDF del fabricante): sube y guarda al toque en modo edición
  // Fichas tecnicas con FECHA: historial (las normas cambian con los anios)
  const [datasheets, setDatasheets] = React.useState<Array<{ id: string; url: string; sheetDate?: string | null; originalName?: string | null; createdAt?: string }>>([]);
  const [dsDate, setDsDate] = React.useState<string>(hoyLocal());
  const [uploadingDatasheet, setUploadingDatasheet] = React.useState(false);
  const datasheetInputRef = React.useRef<HTMLInputElement | null>(null);

  const cargarDatasheets = React.useCallback(async () => {
    if (!isEdit || !id) return;
    try {
      const res = await axios.get(`/api/products/${id}/datasheets`);
      setDatasheets(res.data?.datasheets || []);
    } catch { setDatasheets([]); }
  }, [isEdit, id]);

  useEffect(() => { cargarDatasheets(); }, [cargarDatasheets]);

  const handleDatasheetSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !id) return;
    setUploadingDatasheet(true);
    try {
      const formData = new FormData();
      formData.append('datasheet', file);
      formData.append('sheetDate', dsDate || '');
      await axios.post(`/api/products/${id}/datasheets`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000
      });
      toast.success('Ficha tecnica agregada al historial');
      await cargarDatasheets();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Error al subir la ficha tecnica');
    } finally {
      setUploadingDatasheet(false);
      if (datasheetInputRef.current) datasheetInputRef.current.value = '';
    }
  };

  const eliminarDatasheet = async (dsId: string) => {
    if (!id) return;
    try {
      await axios.delete(`/api/products/${id}/datasheets/${dsId}`);
      await cargarDatasheets();
    } catch { toast.error('No se pudo eliminar la ficha'); }
  };

  // Aplicaciones recomendadas por el fabricante (Recommended Applications)
  const [appInput, setAppInput] = React.useState('');
  const agregarApps = () => {
    const texto = appInput.trim().slice(0, 60);
    if (!texto) return;
    const actuales = new Set((form.recommendedApps || []).map((t) => t.toLowerCase()));
    if (!actuales.has(texto.toLowerCase())) handleChange('recommendedApps', [...(form.recommendedApps || []), texto] as any);
    setAppInput('');
  };
  // Copiar specs/aplicaciones/fichas a las otras capacidades del mismo aceite.
  // El boton primero PREVISUALIZA: lista pre-marcada, se puede destildar y sumar a mano.
  const [copiandoHermanos, setCopiandoHermanos] = React.useState(false);
  const [hermanosOpen, setHermanosOpen] = React.useState(false);
  const [hermanosLista, setHermanosLista] = React.useState<Array<{ id: string; name: string; checked: boolean; manual?: boolean }>>([]);
  const [buscaHermano, setBuscaHermano] = React.useState('');
  const [resultadosHermano, setResultadosHermano] = React.useState<Array<{ id: string; name: string }>>([]);

  const abrirHermanos = async () => {
    if (!id) return;
    try {
      const res = await axios.get(`/api/products/${id}/siblings`);
      const sibs = (res.data?.siblings || []) as Array<{ id: string; name: string }>;
      setHermanosLista(sibs.map((h) => ({ ...h, checked: true })));
      setBuscaHermano('');
      setResultadosHermano([]);
      setHermanosOpen(true);
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'No se pudieron buscar las otras capacidades');
    }
  };

  useEffect(() => {
    if (!hermanosOpen) return;
    const q = buscaHermano.trim();
    if (q.length < 2) { setResultadosHermano([]); return; }
    const t = setTimeout(() => {
      axios.get('/api/products', { params: { search: q, limit: 8, type: 'product' } })
        .then((r) => {
          const ya = new Set([id, ...hermanosLista.map((h) => h.id)]);
          setResultadosHermano(((r.data?.products || []) as any[])
            .filter((prod) => !ya.has(prod.id))
            .map((prod) => ({ id: prod.id, name: prod.name })));
        })
        .catch(() => setResultadosHermano([]));
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buscaHermano, hermanosOpen, hermanosLista]);

  const toggleHermano = (hid: string, checked: boolean) => {
    setHermanosLista((prev) => prev.map((h) => (h.id === hid ? { ...h, checked } : h)));
  };

  const sumarHermano = (prod: { id: string; name: string }) => {
    setHermanosLista((prev) => [...prev, { ...prod, checked: true, manual: true }]);
    setBuscaHermano('');
    setResultadosHermano([]);
  };

  const confirmarHermanos = async () => {
    const ids = hermanosLista.filter((h) => h.checked).map((h) => h.id);
    if (!ids.length) { toast.info('No hay productos marcados para aplicar'); return; }
    setCopiandoHermanos(true);
    try {
      const res = await axios.post(`/api/products/${id}/copy-specs-to-siblings`, { targetIds: ids });
      const { count, names } = res.data || {};
      toast.success(`Aplicado a ${count} producto(s): ${(names || []).join(', ')}`, { duration: 7000 });
      setHermanosOpen(false);
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'No se pudo copiar a las otras capacidades');
    } finally { setCopiandoHermanos(false); }
  };

  const quitarApp = (tag: string) => {
    handleChange('recommendedApps', (form.recommendedApps || []).filter((t) => t !== tag) as any);
  };

  // ── Especificaciones como etiquetas: buscador de existentes + crear nuevas ──
  const [tagInput, setTagInput] = React.useState('');
  const [catalogoTags, setCatalogoTags] = React.useState<Array<{ tag: string; n: number }>>([]);
  const [tagInputFocus, setTagInputFocus] = React.useState(false);

  useEffect(() => {
    const cat = form.category || '';
    axios.get('/api/products/tags-catalog', { params: cat ? { category: cat } : {} })
      .then((r) => setCatalogoTags(r.data?.tags || []))
      .catch(() => setCatalogoTags([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.category]);

  const sugerenciasTags = React.useMemo(() => {
    const puestas = new Set((form.tags || []).map((t) => t.toLowerCase()));
    const q = tagInput.trim().toLowerCase();
    return catalogoTags
      .filter(({ tag }) => !puestas.has(tag.toLowerCase()))
      .filter(({ tag }) => !q || tag.toLowerCase().includes(q))
      .slice(0, 8);
  }, [catalogoTags, form.tags, tagInput]);

  const elegirTagExistente = (tag: string) => {
    const actuales = new Set((form.tags || []).map((t) => t.toLowerCase()));
    if (!actuales.has(tag.toLowerCase())) handleChange('tags', [...(form.tags || []), tag] as any);
    setTagInput('');
  };

  // Una etiqueta por vez, TAL CUAL se tipea: acá no se parte nada (el parser
  // que abre "API SN/CF" en dos queda solo para el import de listas).
  const agregarEtiquetas = () => {
    const texto = tagInput.trim().slice(0, 60);
    if (!texto) return;
    const actuales = new Set((form.tags || []).map((t) => t.toLowerCase()));
    if (!actuales.has(texto.toLowerCase())) handleChange('tags', [...(form.tags || []), texto] as any);
    setTagInput('');
  };

  const quitarEtiqueta = (tag: string) => {
    handleChange('tags', (form.tags || []).filter((t) => t !== tag) as any);
  };

  const removeImage = () => {
    setImagePreview(null);
    handleChange('imageUrl', '');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const saveProduct = async () => {
    if (!form.name || !form.category) {
      toast.error('Nombre y categoría son obligatorios');
      return;
    }

    const isService = form.category === 'servicio' || form.isService;
    const includeFinancials = showFinancials && !isService;
    const rawCost = form.costPrice as any;
    const rawListPrice = form.listPrice as any;
    const rawLastCost = form.lastCost as any;
    const payload: any = {
      name: form.name,
      description: form.description || '',
      brand: isService ? undefined : (form.brand || ''),
      model: isService ? undefined : (form.model || ''),
      tireMeasure: !isService && isTireCategory ? (form.tireMeasure || '') : undefined,
      loadIndex: !isService && isTireCategory ? (form.loadIndex || '') : undefined,
      speedIndex: !isService && isTireCategory ? (form.speedIndex || '') : undefined,
      vehicleFitment: form.vehicleFitment ?? null,
      category: form.category,
      sku: form.sku && form.sku.trim() !== '' ? form.sku : undefined,
      salePrice: Number(form.salePrice || 0),
      costPrice: isService ? undefined : (rawCost === undefined || rawCost === null || rawCost === '' ? undefined : Number(rawCost)),
      listPrice: isService ? undefined : (rawListPrice === undefined || rawListPrice === null || rawListPrice === '' ? undefined : Number(rawListPrice)),
      lastCost: includeFinancials ? (rawLastCost === undefined || rawLastCost === null || rawLastCost === '' ? undefined : Number(rawLastCost)) : undefined,
      mainSupplierName: includeFinancials ? (form.mainSupplierName || '') : undefined,
      supplierSku: includeFinancials ? (form.supplierSku || '') : undefined,
      stock: isService ? undefined : Number(form.stock || 0),
      minStock: isService ? undefined : Number(form.minStock || 0),
      unit: isService ? undefined : form.unit || '',
      location: isService ? undefined : form.location || '',
      requiredRole: isService ? (form.requiredRole || '') : undefined,
      requiredSkills: isService ? (form.requiredSkills || []) : undefined,
      requiredStaff: isService ? Number(form.requiredStaff || 1) : undefined,
      status: form.status || 'active',
      isActive: form.status !== 'paused' && form.status !== 'discontinued',
      imageUrl: form.imageUrl || null,
      variantGroupId: isService ? undefined : (form.variantGroupId || null),
      variantLabel: isService ? undefined : (form.variantLabel || null),
      variantAttributes: isService ? undefined : (form.variantAttributes || {}),
      isBundle: Boolean(form.isBundle),
      bundleItems: Array.isArray(form.bundleItems) ? form.bundleItems : [],
      bundlePriceMode: form.bundlePriceMode || 'fixed',
      // Campos especificos por categoria
      viscosity: isOilCategory ? (form.viscosity || '') : undefined,
      oilBaseType: isOilCategory ? (form.oilBaseType || '') : undefined,
      capacity: (isOilCategory || isLubricantCategory) ? (form.capacity || '') : undefined,
      apiSpec: isOilCategory ? (form.apiSpec || '') : undefined,
      datasheetUrl: form.datasheetUrl || '',
      tags: form.tags || [],
      recommendedApps: form.recommendedApps || [],
      lubricantType: isLubricantCategory ? (form.lubricantType || '') : undefined,
      partNumber: isPartCategory ? (form.partNumber || '') : undefined,
      estimatedDuration: isService ? (form.estimatedDuration ?? null) : undefined,
      workshopStationKey: isService ? (form.workshopStationKey || 'taller') : null,
      isService
    };

    if (payload.isBundle && payload.bundlePriceMode === 'sum') {
      const sumPrice = (payload.bundleItems || []).reduce((acc: number, item: any) => {
        const unitPrice = Number(item.unitPrice || 0);
        return acc + unitPrice * Number(item.qty || 1);
      }, 0);
      payload.salePrice = Number(sumPrice.toFixed(2));
    }

    setSaving(true);
    try {
      if (isEdit && id) {
        await axios.put(`/api/products/${id}`, payload);
        toast.success('Producto actualizado');
      } else {
        await axios.post('/api/products', payload);
        toast.success('Producto creado');
      }
      navigate(backTarget);
    } catch (error: any) {
      console.error('Error saving product', error);
      toast.error(error?.response?.data?.error || 'No se pudo guardar el producto');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto w-full max-w-4xl px-4 py-6 space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(backTarget)} className="gap-2">
            <ArrowLeftIcon className="h-4 w-4" />
            Volver
          </Button>
          <h1 className="text-2xl font-bold">{isEdit ? 'Editar' : 'Nuevo'} Producto/Servicio</h1>
        </div>

        <Card className="shadow-sm">
          <CardContent className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre *</Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Nombre del producto o servicio"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sku">SKU</Label>
                <Input
                  id="sku"
                  value={form.sku || ''}
                  onChange={(e) => handleChange('sku', e.target.value)}
                  placeholder="SKU"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Tipo *</Label>
                <Select
                  value={isServiceType ? 'servicio' : 'producto'}
                  onValueChange={(value) => {
                    if (value === 'servicio') {
                      setForm((prev) => ({ ...prev, isService: true, category: 'servicio', workshopStationKey: prev.workshopStationKey || 'taller' }));
                    } else {
                      setForm((prev) => ({ ...prev, isService: false, category: prev.category && prev.category !== 'servicio' ? prev.category : '' }));
                    }
                  }}
                >
                  <SelectTrigger className="h-10 rounded-xl">
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="producto">Producto</SelectItem>
                    <SelectItem value="servicio">Servicio</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{isServiceType ? 'Área *' : 'Subcategoría *'}</Label>
                {isServiceType ? (
                  <Select
                    value={form.workshopStationKey || ''}
                    onValueChange={(value: any) => handleChange('workshopStationKey', value)}
                  >
                    <SelectTrigger className="h-10 rounded-xl">
                      <SelectValue placeholder="Área / estación..." />
                    </SelectTrigger>
                    <SelectContent>
                      {stationOptions.map((s) => (
                        <SelectItem key={s.type} value={s.type}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Select
                    value={form.category && form.category !== 'servicio' ? form.category : ''}
                    onValueChange={(value) => setForm((prev) => ({ ...prev, category: value, isService: false }))}
                  >
                    <SelectTrigger className="h-10 rounded-xl">
                      <SelectValue placeholder="Categoría..." />
                    </SelectTrigger>
                    <SelectContent>
                      {categoryOptions.map((c) => (
                        <SelectItem key={c} value={c}>{prettyCategory(c)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {isServiceType && <p className="text-xs text-muted-foreground">Área del taller a la que va el vehículo.</p>}
              </div>

              <div className="space-y-2">
                <Label>Estado</Label>
                <Select value={form.status || 'active'} onValueChange={(value: any) => handleChange('status', value)}>
                  <SelectTrigger className="h-10 rounded-xl">
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Activo</SelectItem>
                    <SelectItem value="paused">Pausado</SelectItem>
                    <SelectItem value="discontinued">Discontinuado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {!isServiceType && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="brand">Marca</Label>
                  <Input
                    id="brand"
                    value={form.brand || ''}
                    onChange={(e) => handleChange('brand', e.target.value)}
                    placeholder="Ej: Wanli"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="model">Modelo</Label>
                  <Input
                    id="model"
                    value={form.model || ''}
                    onChange={(e) => handleChange('model', e.target.value)}
                    placeholder="Ej: SP026"
                  />
                </div>
              </div>
            )}

            {!isServiceType && isTireCategory && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="tireMeasure">Medida</Label>
                  <Input
                    id="tireMeasure"
                    value={form.tireMeasure || ''}
                    onChange={(e) => handleChange('tireMeasure', e.target.value)}
                    placeholder="Ej: 175/65R14"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="loadIndex">Indice de carga</Label>
                  <Input
                    id="loadIndex"
                    value={form.loadIndex || ''}
                    onChange={(e) => handleChange('loadIndex', e.target.value)}
                    placeholder="Ej: 88"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="speedIndex">Indice de velocidad</Label>
                  <Input
                    id="speedIndex"
                    value={form.speedIndex || ''}
                    onChange={(e) => handleChange('speedIndex', e.target.value)}
                    placeholder="Ej: T"
                  />
                </div>
              </div>
            )}

            {/* Campos especificos para Aceites */}
            {isOilCategory && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-lg border border-border bg-muted/30 p-4">
                <div className="space-y-2 md:col-span-2">
                  <p className="text-sm font-semibold text-foreground">Datos del aceite</p>
                </div>
                <div className="space-y-2">
                  <Label>Viscosidad</Label>
                  <Input value={form.viscosity || ''} onChange={(e) => handleChange('viscosity', e.target.value)} placeholder="Ej: 5W-40, 10W-40" />
                </div>
                <div className="space-y-2">
                  <Label>Tipo de base</Label>
                  <Select value={form.oilBaseType || ''} onValueChange={(value) => handleChange('oilBaseType', value)}>
                    <SelectTrigger className="h-10 rounded-xl"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sintetico">Sintético</SelectItem>
                      <SelectItem value="semisintetico">Semi-sintético</SelectItem>
                      <SelectItem value="mineral">Mineral</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Capacidad</Label>
                  <Input value={form.capacity || ''} onChange={(e) => handleChange('capacity', e.target.value)} placeholder="Ej: 1L, 4L, 5L, 20L" />
                </div>
              </div>
            )}

            {/* Campos especificos para Lubricantes / Liquidos */}
            {isLubricantCategory && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-lg border border-border bg-muted/30 p-4">
                <div className="space-y-2 md:col-span-2">
                  <p className="text-sm font-semibold text-foreground">Datos del lubricante</p>
                </div>
                <div className="space-y-2">
                  <Label>Tipo</Label>
                  <Input value={form.lubricantType || ''} onChange={(e) => handleChange('lubricantType', e.target.value)} placeholder="Ej: Frenos DOT4, Refrigerante, Hidráulico, Grasa" />
                </div>
                <div className="space-y-2">
                  <Label>Capacidad / Volumen</Label>
                  <Input value={form.capacity || ''} onChange={(e) => handleChange('capacity', e.target.value)} placeholder="Ej: 500ml, 1L, 5L" />
                </div>
              </div>
            )}

            {/* Campos especificos para Repuestos */}
            {isPartCategory && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-lg border border-border bg-muted/30 p-4">
                <div className="space-y-2 md:col-span-2">
                  <p className="text-sm font-semibold text-foreground">Datos del repuesto</p>
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Número de parte / OEM</Label>
                  <Input value={form.partNumber || ''} onChange={(e) => handleChange('partNumber', e.target.value)} placeholder="Ej: 90915-YZZD2" />
                </div>
              </div>
            )}

            {/* Especificaciones y aplicaciones: para todo producto (no solo aceites) */}
            {String(form.category || '').toLowerCase() !== 'servicio' && (
              <div className="grid grid-cols-1 gap-4 rounded-lg border border-border bg-muted/30 p-4">
                <p className="text-sm font-semibold text-foreground">Especificaciones y aplicaciones</p>
                <div className="space-y-2 md:col-span-2">
                  <div className="flex items-baseline justify-between gap-2">
                    <Label>Especificaciones de este producto (etiquetas)</Label>
                    {form.specsUpdatedAt && (
                      <span className="text-xs text-muted-foreground">
                        Última edición: {String(form.specsUpdatedAt).slice(0, 10).split('-').reverse().join('/')}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Input
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onFocus={() => setTagInputFocus(true)}
                        onBlur={() => setTimeout(() => setTagInputFocus(false), 150)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); agregarEtiquetas(); } }}
                        placeholder="Buscar etiqueta existente o escribir una nueva..."
                      />
                      {tagInputFocus && sugerenciasTags.length > 0 && (
                        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-border bg-popover shadow-md">
                          {sugerenciasTags.map(({ tag, n }) => (
                            <button
                              key={tag}
                              type="button"
                              onMouseDown={(e) => { e.preventDefault(); elegirTagExistente(tag); }}
                              className="flex w-full items-center justify-between px-3 py-1.5 text-left text-sm hover:bg-accent"
                            >
                              <span>{tag}</span>
                              <span className="text-xs text-muted-foreground">{n} prod.</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button type="button" variant="outline" onClick={agregarEtiquetas} disabled={!tagInput.trim()} className="shrink-0">
                      Agregar nueva
                    </Button>
                  </div>
                  {(form.tags || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {(form.tags || []).map((tag) => (
                        <span key={tag} className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs text-foreground">
                          {tag}
                          <button type="button" onClick={() => quitarEtiqueta(tag)} className="text-muted-foreground hover:text-destructive">×</button>
                        </span>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">El buscador ofrece las etiquetas que ya existen en el rubro (click para agregarla). Con "Agregar nueva" se crea la tipeada TAL CUAL, de a una. Abajo, las de ESTE producto (la × las quita).</p>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>Aplicaciones recomendadas por el fabricante</Label>
                  <div className="flex gap-2">
                    <Input
                      value={appInput}
                      onChange={(e) => setAppInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); agregarApps(); } }}
                      placeholder='Una por vez, ej: Ford WSS-M2C972-A1'
                    />
                    <Button type="button" variant="outline" onClick={agregarApps} disabled={!appInput.trim()} className="shrink-0">
                      Agregar
                    </Button>
                  </div>
                  {(form.recommendedApps || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {(form.recommendedApps || []).map((tag) => (
                        <span key={tag} className="inline-flex items-center gap-1 rounded-md border border-sky-500/40 bg-sky-500/10 px-2 py-0.5 text-xs text-sky-700 dark:text-sky-300">
                          {tag}
                          <button type="button" onClick={() => quitarApp(tag)} className="text-muted-foreground hover:text-destructive">×</button>
                        </span>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">El "Recommended Applications" de la hoja técnica: normas que el fabricante recomienda sin licencia formal. Se cargan de a una, tal cual.</p>
                  {isEdit && (
                    <div className="pt-1">
                      <Button type="button" variant="outline" size="sm" onClick={abrirHermanos} disabled={copiandoHermanos} className="rounded-xl">
                        Aplicar a las otras capacidades de este producto
                      </Button>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Guardá primero este producto; después copia especificaciones, aplicaciones, fichas y vehículos compatibles a los que elijas.
                      </p>
                      <Dialog open={hermanosOpen} onOpenChange={setHermanosOpen}>
                        <DialogContent className="sm:max-w-md">
                          <DialogHeader>
                            <DialogTitle>Aplicar especificaciones a otros productos</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-3">
                            <p className="text-sm text-muted-foreground">
                              Copia las etiquetas, aplicaciones, fichas y vehículos compatibles de <span className="font-medium text-foreground">{form.name}</span> a los productos marcados.
                            </p>
                            {hermanosLista.length > 0 ? (
                              <div className="max-h-52 space-y-1 overflow-y-auto rounded-lg border p-2">
                                {hermanosLista.map((h) => (
                                  <label key={h.id} className="flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1 text-sm hover:bg-muted/50">
                                    <Checkbox checked={h.checked} onCheckedChange={(v) => toggleHermano(h.id, v === true)} />
                                    <span className="min-w-0 truncate">{h.name}</span>
                                    {h.manual && <span className="shrink-0 text-[11px] text-muted-foreground">agregado a mano</span>}
                                  </label>
                                ))}
                              </div>
                            ) : (
                              <p className="rounded-lg border border-dashed p-3 text-sm text-muted-foreground">
                                No se detectaron otras capacidades (misma marca, modelo y viscosidad). Buscá abajo para elegirlas a mano.
                              </p>
                            )}
                            <div className="relative">
                              <Input
                                value={buscaHermano}
                                onChange={(e) => setBuscaHermano(e.target.value)}
                                placeholder="Buscar otro producto para sumarlo..."
                                className="h-8"
                              />
                              {resultadosHermano.length > 0 && (
                                <div className="absolute z-50 mt-1 max-h-44 w-full overflow-y-auto rounded-lg border bg-popover p-1 shadow-md">
                                  {resultadosHermano.map((prod) => (
                                    <button
                                      key={prod.id}
                                      type="button"
                                      onMouseDown={(e) => { e.preventDefault(); sumarHermano(prod); }}
                                      className="block w-full truncate rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted"
                                    >
                                      {prod.name}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setHermanosOpen(false)} className="rounded-xl">Cancelar</Button>
                            <Button type="button" onClick={confirmarHermanos} disabled={copiandoHermanos || hermanosLista.every((h) => !h.checked)} className="rounded-xl">
                              {copiandoHermanos ? 'Aplicando...' : `Aplicar a ${hermanosLista.filter((h) => h.checked).length} producto(s)`}
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="description">Descripción</Label>
              <Input
                id="description"
                value={form.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Descripción del producto o servicio"
              />
            </div>

            <div className="space-y-2">
              <Label>Vehículos compatibles (opcional)</Label>
              <VehicleFitmentInput
                value={form.vehicleFitment}
                onChange={(lista) => handleChange('vehicleFitment', lista as any)}
              />
            </div>

            <div className="space-y-3">
              <Label>Foto del producto</Label>
              <div className="flex flex-wrap items-center gap-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageSelect}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingImage}
                  className="rounded-xl"
                >
                  <PhotoIcon className={`h-4 w-4 mr-2 ${uploadingImage ? 'animate-pulse' : ''}`} />
                  {uploadingImage ? 'Subiendo...' : imagePreview ? 'Cambiar foto' : 'Cargar foto'}
                </Button>
                {imagePreview && (
                  <Button type="button" variant="ghost" className="text-destructive" onClick={removeImage}>
                    <XMarkIcon className="h-4 w-4 mr-1" />
                    Quitar
                  </Button>
                )}
              </div>
              {imagePreview && (
                <div className="relative h-32 w-32 overflow-hidden rounded-xl border border-border bg-muted">
                  <img src={getImageUrl(imagePreview)} alt="Foto del producto" className="h-full w-full object-cover" />
                </div>
              )}
              <p className="text-xs text-muted-foreground">Formatos PNG/JPG, hasta 5MB.</p>
            </div>

            <div className="space-y-3">
              <Label>Fichas técnicas (historial por fecha)</Label>
              {!isEdit ? (
                <p className="text-xs text-muted-foreground">Guardá el producto primero; después vas a poder subir sus fichas técnicas.</p>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      ref={datasheetInputRef}
                      type="file"
                      accept="application/pdf,image/*"
                      className="hidden"
                      onChange={handleDatasheetSelect}
                    />
                    <DatePicker value={dsDate} onChange={setDsDate} className="h-9 w-[150px]" />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => datasheetInputRef.current?.click()}
                      disabled={uploadingDatasheet}
                      className="rounded-xl"
                    >
                      {uploadingDatasheet ? 'Subiendo...' : 'Subir ficha (PDF)'}
                    </Button>
                  </div>
                  {datasheets.length > 0 && (
                    <div className="divide-y divide-border rounded-xl border border-border">
                      {datasheets.map((d) => (
                        <div key={d.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                          <span className="w-24 shrink-0 font-medium tabular-nums">
                            {(d.sheetDate || (d.createdAt || '').slice(0, 10)).split('-').reverse().join('/')}
                          </span>
                          <a href={getImageUrl(d.url)} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate text-primary underline decoration-dotted hover:opacity-80">
                            {d.originalName || 'Ver ficha técnica'}
                          </a>
                          <button type="button" onClick={() => eliminarDatasheet(d.id)} className="shrink-0 text-muted-foreground hover:text-destructive">
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Las especificaciones cambian con los años: cada ficha se guarda con su fecha, la más nueva arriba. PDF o imagen, hasta 15MB.
                  </p>
                </>
              )}
            </div>


            {/* Duracion estimada para Servicios */}
            {isServiceType && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Duración estimada (minutos)</Label>
                  <Input
                    type="number"
                    min={1}
                    value={form.estimatedDuration ?? ''}
                    onChange={(e) => handleChange('estimatedDuration', e.target.value ? Number(e.target.value) : undefined)}
                    placeholder="60"
                  />
                </div>
              </div>
            )}

            {!isServiceType && (
              <>
                <div className="space-y-2">
                  <Label>Unidad</Label>
                  <Select value={form.unit || ''} onValueChange={(value) => handleChange('unit', value)}>
                    <SelectTrigger className="h-10 rounded-xl">
                      <SelectValue placeholder="Seleccionar..." />
                    </SelectTrigger>
                    <SelectContent>
                      {units.map((u) => (
                        <SelectItem key={u} value={u}>{u}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Ubicacion</Label>
                  <Select
                    value={form.location || ''}
                    onValueChange={(value) => handleChange('location', value)}
                  >
                    <SelectTrigger className="h-10 rounded-xl">
                      <SelectValue placeholder="Seleccionar..." />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.length ? (
                        locations.map((loc) => (
                          <SelectItem key={loc} value={loc}>
                            {loc}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="no-locations" disabled>
                          Sin ubicaciones configuradas
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>Costo unitario</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.costPrice ?? ''}
                      onChange={(e) => {
                        const newCost = parseFloat(e.target.value) || 0;
                        const currentMarkup = Number((form as any).markupPercent || 0);
                        const currentSale = Number(form.salePrice || 0);
                        if (newCost > 0 && currentMarkup > 0) {
                          // Hay costo y markup → recalcular precio de venta
                          const newSale = Math.round(newCost * (1 + currentMarkup / 100) * 100) / 100;
                          setForm((prev) => ({ ...prev, costPrice: newCost, salePrice: newSale }));
                        } else if (newCost > 0 && currentSale > 0) {
                          // Hay costo y precio venta → recalcular markup
                          const newMarkup = Math.round(((currentSale - newCost) / newCost) * 10000) / 100;
                          setForm((prev) => ({ ...prev, costPrice: newCost, markupPercent: newMarkup } as any));
                        } else {
                          handleChange('costPrice', e.target.value);
                        }
                      }}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>% Markup</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={(form as any).markupPercent ?? ''}
                      onChange={(e) => {
                        const newMarkup = parseFloat(e.target.value) || 0;
                        const currentCost = Number(form.costPrice || 0);
                        if (currentCost > 0) {
                          const newSale = Math.round(currentCost * (1 + newMarkup / 100) * 100) / 100;
                          setForm((prev) => ({ ...prev, markupPercent: newMarkup, salePrice: newSale } as any));
                        } else {
                          setForm((prev) => ({ ...prev, markupPercent: newMarkup } as any));
                        }
                      }}
                      placeholder="30.0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Precio de venta *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.salePrice ?? 0}
                      onChange={(e) => {
                        const newSale = parseFloat(e.target.value) || 0;
                        const currentCost = Number(form.costPrice || 0);
                        if (currentCost > 0 && newSale > 0) {
                          // Recalcular markup desde el nuevo precio de venta
                          const newMarkup = Math.round(((newSale - currentCost) / currentCost) * 10000) / 100;
                          setForm((prev) => ({ ...prev, salePrice: newSale, markupPercent: newMarkup } as any));
                        } else {
                          handleChange('salePrice', e.target.value);
                        }
                      }}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Precio de lista</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.listPrice ?? ''}
                      onChange={(e) => handleChange('listPrice', e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                </div>

                {showFinancials && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Ultimo costo</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={form.lastCost ?? ''}
                        onChange={(e) => handleChange('lastCost', e.target.value)}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Proveedor principal</Label>
                      <ProviderCombobox
                        providers={providers}
                        selectedProvider={providers.find((p) => p.name === form.mainSupplierName) || null}
                        onSelect={(provider) => handleChange('mainSupplierName', provider ? provider.name : '')}
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label>{'Código del proveedor'}</Label>
                      <Input
                        value={form.supplierSku || ''}
                        onChange={(e) => handleChange('supplierSku', e.target.value)}
                        placeholder="El que figura en su lista o factura. Ej: 002235"
                      />
                      <p className="text-xs text-muted-foreground">
                        {'Con este código el sistema reconoce el producto cuando importás la lista de ese proveedor, en vez de crear uno duplicado.'}
                      </p>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Stock actual</Label>
                    <Input
                      type="number"
                      value={form.stock ?? 0}
                      readOnly
                      disabled
                      className="bg-muted cursor-not-allowed"
                      placeholder="0"
                    />
                    <p className="text-xs text-muted-foreground">Solo se modifica por compras, remitos o ajustes manuales.</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Stock mínimo</Label>
                    <Input
                      type="number"
                      value={form.minStock ?? 0}
                      onChange={(e) => handleChange('minStock', e.target.value)}
                      placeholder="0"
                    />
                  </div>
                </div>
              </>
            )}

            {isServiceType && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Precio de venta *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.salePrice ?? 0}
                    onChange={(e) => handleChange('salePrice', e.target.value)}
                    placeholder="0.00"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Personal requerido</Label>
                  <Input
                    type="number"
                    min={1}
                    value={form.requiredStaff ?? 1}
                    onChange={(e) => handleChange('requiredStaff', e.target.value)}
                    placeholder="1"
                    className="max-w-[120px]"
                  />
                  <p className="text-xs text-muted-foreground">Cantidad de personas necesarias para realizar el servicio.</p>
                </div>

                <div className="space-y-2">
                  <Label>Skills requeridas</Label>
                  {(() => {
                    const selected = Array.isArray(form.requiredSkills) ? form.requiredSkills : [];
                    const available = skillOptions.filter((s) => s.isActive !== false && !selected.includes(s.value));
                    const filtered = available.filter((s) => s.label.toLowerCase().includes(skillQuery.trim().toLowerCase()));
                    return (
                      <>
                        <Popover open={skillPickerOpen} onOpenChange={(o) => { setSkillPickerOpen(o); if (!o) setSkillQuery(''); }}>
                          <PopoverTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              role="combobox"
                              className="w-full justify-between font-normal text-muted-foreground"
                            >
                              Agregar skill...
                              <ChevronUpDownIcon className="h-4 w-4 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="p-0" align="start" style={{ width: 'var(--radix-popover-trigger-width)' }}>
                            <div className="border-b p-2">
                              <Input
                                autoFocus
                                placeholder="Buscar skill..."
                                value={skillQuery}
                                onChange={(e) => setSkillQuery(e.target.value)}
                                className="h-8"
                              />
                            </div>
                            <div className="max-h-[300px] overflow-y-auto p-1">
                              {available.length === 0 ? (
                                <div className="px-2 py-3 text-center text-sm text-muted-foreground">No hay mas skills para agregar</div>
                              ) : filtered.length === 0 ? (
                                <div className="px-2 py-3 text-center text-sm text-muted-foreground">No se encontraron skills</div>
                              ) : (
                                filtered.map((s) => (
                                  <button
                                    key={s.value}
                                    type="button"
                                    onClick={() => addRequiredSkill(s.value)}
                                    className="flex w-full items-center rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
                                  >
                                    {s.label}
                                  </button>
                                ))
                              )}
                            </div>
                          </PopoverContent>
                        </Popover>
                        {selected.length > 0 && (
                          <div className="flex flex-wrap gap-2 pt-1">
                            {selected.map((value) => {
                              const opt = skillOptions.find((s) => s.value === value);
                              const label = opt?.label || value;
                              const inactive = opt ? opt.isActive === false : true;
                              return (
                                <span key={value} className="inline-flex items-center gap-1 rounded-full border bg-muted px-2.5 py-1 text-sm">
                                  {label}{inactive ? ' (inactiva)' : ''}
                                  <Tip label="Quitar skill">
                                    <button
                                      type="button"
                                      onClick={() => toggleRequiredSkill(value)}
                                      className="text-muted-foreground hover:text-foreground"
                                    >
                                      <XMarkIcon className="h-3.5 w-3.5" />
                                    </button>
                                  </Tip>
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Combo de SERVICIOS ("Detailing T2"): en el remito/presupuesto va
                UN renglón con UN precio y las inclusiones como viñetas adentro
                (variante C). No expande items ni descuenta stock: por eso solo
                se ofrece para servicios. */}
            {isServiceType && (
            <div className="space-y-3 rounded-xl border p-4">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={Boolean(form.isBundle)}
                  onCheckedChange={(checked) => handleChange('isBundle', Boolean(checked))}
                />
                <span>Es un combo (el remito muestra qué incluye)</span>
              </label>

              {form.isBundle && (
                <div className="space-y-3">
                  <p className="text-xs text-muted-foreground">
                    Al cargar el combo en un remito o presupuesto aparecen estas inclusiones tildadas:
                    destildás lo que no se haga en esa visita. Se cobra solo el precio del combo.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="md:col-span-3">
                      <Label>Inclusión (texto libre)</Label>
                      <Input
                        value={bundleCandidateText}
                        onChange={(e) => setBundleCandidateText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addBundleTexto(); } }}
                        placeholder="Ej: Lavado de motor"
                      />
                    </div>
                    <div className="flex items-end">
                      <Button type="button" variant="outline" className="w-full" onClick={addBundleTexto}>
                        Agregar
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="md:col-span-2">
                      <Label>O un servicio del catálogo</Label>
                      <Select value={bundleCandidateId} onValueChange={setBundleCandidateId}>
                        <SelectTrigger className="h-10 rounded-xl">
                          <SelectValue placeholder="Seleccionar producto/servicio..." />
                        </SelectTrigger>
                        <SelectContent>
                          {allProducts.map((item) => (
                            <SelectItem key={item.id} value={item.id}>
                              {item.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Cantidad</Label>
                      <Input
                        type="number"
                        min={1}
                        value={bundleCandidateQty}
                        onChange={(e) => setBundleCandidateQty(e.target.value)}
                      />
                    </div>
                    <div className="flex items-end">
                      <Button type="button" variant="outline" className="w-full" onClick={addBundleItem}>
                        Agregar
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    {(form.bundleItems || []).length ? (
                      (form.bundleItems || []).map((item) => (
                        <div key={item.productId} className="flex items-center justify-between rounded-lg border px-3 py-2 text-sm">
                          <span>
                            {item.name} x{item.qty}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            className="h-7 px-2 text-destructive"
                            onClick={() => removeBundleItem(item.productId)}
                          >
                            Quitar
                          </Button>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground">Sin items cargados.</p>
                    )}
                  </div>

                  <p className="text-xs text-muted-foreground">
                    El precio del combo es el precio de venta de este servicio (las inclusiones no suman aparte).
                  </p>
                </div>
              )}
            </div>
            )}

            {isEdit && (
              <div className="space-y-2 rounded-xl border p-4">
                <h3 className="text-sm font-semibold">Historial de costos</h3>
                {costHistory.length ? (
                  <div className="space-y-2">
                    {costHistory.map((entry) => {
                      const inferredProvider = inferHistoryProvider(entry);
                      const isVigente = entry.id === vigenteHistoryId;
                      return (
                      <div
                        key={entry.id}
                        className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm ${
                          isVigente ? 'border-green-500/60 bg-green-500/10 text-foreground' : ''
                        }`}
                      >
                        <span className={isVigente ? 'font-semibold' : ''}>
                          ${Number(entry.costPrice || 0).toLocaleString('es-AR')}
                          {entry.previousCostPrice !== undefined && entry.previousCostPrice !== null
                            ? ` (antes $${Number(entry.previousCostPrice).toLocaleString('es-AR')})`
                            : ''}
                          {inferredProvider ? ` - ${inferredProvider}` : ''}
                          {isVigente ? ' - VIGENTE' : ''}
                        </span>
                        <span className="text-muted-foreground">{new Date(entry.createdAt).toLocaleDateString('es-AR')}</span>
                      </div>
                    )})}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Sin historial de costos.</p>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => navigate(backTarget)}>
                Cancelar
              </Button>
              <Button onClick={saveProduct} disabled={saving}>
                {saving ? 'Guardando...' : isEdit ? 'Actualizar' : 'Crear'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProductFormPage;
